'use client';

import { Media } from '@/components/common/Media/Media';
import { useCartStore } from '@/features/cart/store/cart';
import { debounce } from '@/utils/debounce';
import { Badge } from '@andritzot/metris-web-ui/data-display/badge';
import { Tooltip } from '@andritzot/metris-web-ui/data-display/tooltip';
import { Button } from '@andritzot/metris-web-ui/inputs/button';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import ArrowForwardOutlined from '@mui/icons-material/ArrowForwardOutlined';
import FilterListOutlined from '@mui/icons-material/FilterListOutlined';
import HelpOutlineOutlined from '@mui/icons-material/HelpOutlineOutlined';
import { usePiwikPro } from '@piwikpro/next-piwik-pro';
import { useRouter } from 'next/navigation';
import { InputText } from 'primereact/inputtext';
import { SelectButton } from 'primereact/selectbutton';
import { useCallback, useMemo, useState } from 'react';

import { useHelpWizardStore } from '../../store/helpWizard';
import { useSolutionStore } from '../../store/solution';

const layoutOptions = [
  { icon: 'pi pi-th-large', value: 'card' },
  { icon: 'pi pi-table', value: 'grid' },
];

const itemTemplate = (option: { icon: string }) => <i className={option.icon} />;

const Header = () => {
  const [searchText, setSearchText] = useState('');
  const isSidebarOpen = useSolutionStore((s) => s.isSidebarOpen);
  const layout = useSolutionStore((s) => s.layout);
  const cartProducts = useCartStore((s) => s.cartProducts);
  const router = useRouter();
  const { SiteSearch } = usePiwikPro();

  const handleOpenSidebar = () => useSolutionStore.setState({ isSidebarOpen: true });
  const handleOpenHelpDialog = () => useHelpWizardStore.setState({ isDialogOpen: true });

  const debouncedSearchActions = useMemo(
    () =>
      debounce((value: string) => {
        SiteSearch.trackSiteSearch(value);
        useSolutionStore.setState({ searchValue: value });
      }, 300),
    [SiteSearch],
  );

  const handleSearchText = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(e.target.value);
      debouncedSearchActions(e.target.value);
    },
    [debouncedSearchActions],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
      <Media lessThan="md">
        <Button
          variant="outlined"
          sx={{ display: 'flex', width: '100%', mb: '5px' }}
          endIcon={<HelpOutlineOutlined />}
          onClick={handleOpenHelpDialog}
        >
          Need help?
        </Button>
        <Box sx={{ width: '100%', my: '5px', '& span:first-of-type, & input': { width: '100%' } }}>
          <div className="p-inputgroup">
            <span className="p-input-icon-left">
              <i className="pi pi-search" />
              <InputText
                data-testid="search-input"
                type="search"
                value={searchText}
                onChange={handleSearchText}
                placeholder="Search"
              />
            </span>
          </div>
        </Box>
      </Media>
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <Box sx={{ flex: 1, display: 'flex' }}>
          {!isSidebarOpen && (
            <Media greaterThanOrEqual="md">
              <Button variant="outlined" startIcon={<FilterListOutlined />} onClick={handleOpenSidebar} sx={{ mr: 2 }}>
                Filters
              </Button>
            </Media>
          )}
          <Media lessThan="md">
            <Button variant="outlined" endIcon={<ArrowForwardOutlined />} onClick={handleOpenSidebar}>
              Filters
            </Button>
          </Media>

          <Media greaterThanOrEqual="md">
            {(className, renderChildren) => (
              <Box
                className={className}
                sx={{
                  width: '100%',
                  my: '5px',
                  '@media (min-width: 768px)': { my: 0, pr: '2rem' },
                  '& span:first-of-type, & input': { width: '100%' },
                }}
              >
                {renderChildren ? (
                  <div className="p-inputgroup">
                    <span className="p-input-icon-left">
                      <i className="pi pi-search" />
                      <InputText
                        data-testid="search-input"
                        type="search"
                        value={searchText}
                        onChange={handleSearchText}
                        placeholder="Search"
                      />
                    </span>
                  </div>
                ) : null}
              </Box>
            )}
          </Media>
        </Box>
        <SelectButton
          style={{ display: 'flex' }}
          allowEmpty={false}
          value={layout}
          options={layoutOptions}
          onChange={(e) => useSolutionStore.setState({ layout: e.value })}
          itemTemplate={itemTemplate}
        />
        <Box
          sx={{
            whiteSpace: 'nowrap',
            ml: 1,
            display: 'flex',
            alignItems: 'center',
            '& button.p-link': {
              display: 'flex',
              alignItems: 'center',
              '& i': {
                display: 'flex',
                fontSize: '1.5em',
                width: 'auto !important',
                margin: '5px 10px',
                color: 'primary.main',
                '& span': { margin: '0 5px' },
              },
            },
          }}
        >
          {cartProducts.length ? (
            <button
              type="button"
              className="p-link layout-topbar-icon"
              onClick={() => {
                router.push('/solutions/cart');
              }}
            >
              <Badge badgeContent={cartProducts.length} color="success">
                <i className="topbar-icon pi pi-fw pi-shopping-cart" />
              </Badge>
            </button>
          ) : (
            <Tooltip title="Could not find what you were looking for?">
              <Button
                variant="outlined"
                onClick={() => {
                  router.push('/solutions/cart');
                }}
              >
                Contact us
              </Button>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Header;
